/**
 * Main Banner Carousel
 * Custom element for accessible carousel with autoplay
 */
class MainBanner extends HTMLElement {
  constructor() {
    super();

    this.currentSlide = 0;
    this.totalSlides = parseInt(this.dataset.slidesCount, 10);
    this.autoplaySpeed = parseInt(this.dataset.autoplaySpeed, 10) || 15000;
    this.autoplayInterval = null;
    this.isPlaying = false;
    this.autoplayDisabledByUser = false;
    this.autoplayEnabledByUser = false;
    this._initialized = false;

    this.reducedMotionMql =
      typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia('(prefers-reduced-motion: reduce)')
        : null;
    this.prefersReducedMotion = this.reducedMotionMql ? this.reducedMotionMql.matches : false;

    this._onReducedMotionChange = this._onReducedMotionChange.bind(this);
    this._onFocusIn = this._onFocusIn.bind(this);
    this._onFocusOut = this._onFocusOut.bind(this);
    this._onAutoplayBtnClick = this._onAutoplayBtnClick.bind(this);
  }

  connectedCallback() {
    if (this.totalSlides <= 1) {
      return;
    }

    this.init();
  }

  disconnectedCallback() {
    this.stopAutoplay();
    this.removeEventListeners();
    if (this.autoplayBtn) {
      this.autoplayBtn.removeEventListener('click', this._onAutoplayBtnClick, { capture: true });
    }

    if (this.reducedMotionMql) {
      this.reducedMotionMql.removeEventListener('change', this._onReducedMotionChange);
    }
    this.removeEventListener('focusin', this._onFocusIn, true);
  }

  init() {
    this.track = this.querySelector('.main-banner__track');
    this.slides = this.querySelectorAll('.main-banner__slide');
    this.dots = this.querySelectorAll('.main-banner__dot');
    this.prevButton = this.querySelector('.main-banner__arrow--prev');
    this.nextButton = this.querySelector('.main-banner__arrow--next');
    this.tablist = this.querySelector('.main-banner__dots');
    this.autoplayBtn = this.querySelector('.main-banner__autoplay-btn');
    this.statusEl = this.querySelector('[id^="main-banner-status-"]');

    if (this.reducedMotionMql) {
      this.reducedMotionMql.addEventListener('change', this._onReducedMotionChange);
    }

    this.classList.toggle('main-banner--motion-reduced', this.prefersReducedMotion);

    this.addEventListeners();

    this.style.setProperty('--autoplay-duration', `${this.autoplaySpeed}ms`);

    if (this.shouldAutoplay()) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }

    this.updateAutoplayButton();
    this.updateCarousel();
    this._initialized = true;
  }

  shouldAutoplay() {
    if (this.autoplayDisabledByUser) return false;
    if (this.prefersReducedMotion) return this.autoplayEnabledByUser;
    return true;
  }

  _onReducedMotionChange(e) {
    this.prefersReducedMotion = e.matches;
    this.classList.toggle('main-banner--motion-reduced', this.prefersReducedMotion);

    this.autoplayEnabledByUser = false;
    this.autoplayDisabledByUser = false;

    if (this.shouldAutoplay()) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }

    this.updateAutoplayButton();
    this.updateCarousel();
  }

  /**
   * Pausa autoplay al entrar el foco (teclado o cualquier control del carrusel).
   * capture: true para que no dependa solo del burbujeo en el host.
   */
  _onFocusIn(e) {
    const t = e.target;
    if (!t || !this.contains(t)) {
      return;
    }
    this.pauseAutoplay();
  }

  _onFocusOut(e) {
    if (e.relatedTarget && this.contains(e.relatedTarget)) {
      return;
    }
    this.resumeAutoplay();
  }

  _onAutoplayBtnClick(e) {
    e.preventDefault();
    e.stopPropagation();
    this.toggleAutoplayDisabled();
  }

  /**
   * Add all event listeners
   */
  addEventListeners() {
    if (this.prevButton) {
      this.prevButton.addEventListener('click', () => this.goToPrevSlide());
    }
    if (this.nextButton) {
      this.nextButton.addEventListener('click', () => this.goToNextSlide());
    }

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => this.goToSlide(index));
    });

    if (this.tablist) {
      this.tablist.addEventListener('keydown', (e) => this.handleTablistKeydown(e));
    }

    if (this.autoplayBtn) {
      this.autoplayBtn.removeEventListener('click', this._onAutoplayBtnClick, { capture: true });
      this.autoplayBtn.addEventListener('click', this._onAutoplayBtnClick, { capture: true });
    }

    this.addEventListener('keydown', (e) => this.handleKeydown(e));

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.resumeAutoplay());
    this.addEventListener('focusin', this._onFocusIn, true);
    this.addEventListener('focusout', this._onFocusOut);

    let touchStartX = 0;
    let touchEndX = 0;

    this.addEventListener(
      'touchstart',
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    this.addEventListener(
      'touchend',
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        this.handleSwipe(touchStartX, touchEndX);
      },
      { passive: true }
    );
  }

  removeEventListeners() {}

  toggleAutoplayDisabled() {
    if (this.prefersReducedMotion) {
      this.autoplayEnabledByUser = !this.autoplayEnabledByUser;
    } else {
      this.autoplayDisabledByUser = !this.autoplayDisabledByUser;
    }

    if (this.shouldAutoplay()) {
      this.startAutoplay();
    } else {
      this.stopAutoplay();
    }
    this.updateAutoplayButton();
    this.updateCarousel();
  }

  updateAutoplayButton() {
    if (!this.autoplayBtn) {
      return;
    }

    this.autoplayBtn.hidden = false;

    const paused = !this.shouldAutoplay();
    this.autoplayBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
    this.autoplayBtn.setAttribute('aria-label', paused ? 'Play carousel' : 'Pause carousel');
    this.autoplayBtn.classList.toggle('main-banner__autoplay-btn--paused', paused);
  }

  handleKeydown(e) {
    if (this.tablist && this.tablist.contains(e.target)) {
      return;
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      this.goToPrevSlide();
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      this.goToNextSlide();
    }
  }

  handleTablistKeydown(e) {
    if (this.totalSlides <= 1) return;

    let nextIndex = null;

    if (e.key === 'Home') {
      e.preventDefault();
      e.stopPropagation();
      nextIndex = 0;
    } else if (e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      nextIndex = this.totalSlides - 1;
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      e.stopPropagation();
      nextIndex = this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      nextIndex = this.currentSlide === this.totalSlides - 1 ? 0 : this.currentSlide + 1;
    }

    if (nextIndex !== null) {
      this.goToSlide(nextIndex, true);
    }
  }

  handleSwipe(startX, endX) {
    const swipeThreshold = 50;
    const diff = startX - endX;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        this.goToNextSlide();
      } else {
        this.goToPrevSlide();
      }
    }
  }

  goToSlide(index, userInitiated = true) {
    if (index < 0 || index >= this.totalSlides) return;

    if (userInitiated && this.shouldAutoplay()) {
      this.resetAutoplay();
    }

    this.currentSlide = index;
    this.updateCarousel();
  }

  goToPrevSlide() {
    const newIndex = this.currentSlide === 0 ? this.totalSlides - 1 : this.currentSlide - 1;
    this.goToSlide(newIndex, true);
  }

  goToNextSlide() {
    const newIndex = this.currentSlide === this.totalSlides - 1 ? 0 : this.currentSlide + 1;
    this.goToSlide(newIndex, true);
  }

  updateCarousel() {
    const translateX = -this.currentSlide * 100;
    this.track.style.transform = `translateX(${translateX}%)`;

    this.slides.forEach((slide, index) => {
      const isActive = index === this.currentSlide;
      slide.inert = !isActive;
      slide.setAttribute('aria-hidden', (!isActive).toString());
    });

    const container = this.querySelector('.main-banner__container');
    if (container && container.scrollLeft !== 0) {
      container.scrollLeft = 0;
    }

    const allowProgressAnim = this.isPlaying && !this.prefersReducedMotion;

    this.dots.forEach((dot, index) => {
      const isActive = index === this.currentSlide;
      dot.classList.toggle('main-banner__dot--active', isActive);
      dot.setAttribute('aria-selected', isActive.toString());
      dot.setAttribute('tabindex', isActive ? '0' : '-1');

      const progress = dot.querySelector('.main-banner__dot-progress');
      if (progress) {
        if (isActive && allowProgressAnim) {
          progress.style.animation = 'none';
          progress.style.width = '0';
          void progress.offsetWidth;
          requestAnimationFrame(() => {
            progress.style.animation = '';
          });
        } else {
          progress.style.animation = 'none';
          progress.style.width = '0';
        }
      }
    });

    if (this.tablist && this.tablist.contains(document.activeElement)) {
      const activeDot = this.dots[this.currentSlide];
      if (activeDot && document.activeElement !== activeDot) {
        activeDot.focus({ preventScroll: true });
      }
    }

    this.updateArrowVisibility();
    this.updateStatus();
  }

  updateArrowVisibility() {
    if (this.prevButton) {
      this.prevButton.disabled = this.currentSlide === 0;
    }
    if (this.nextButton) {
      this.nextButton.disabled = this.currentSlide === this.totalSlides - 1;
    }
  }

  updateStatus() {
    if (!this._initialized || !this.statusEl) return;
    this.statusEl.textContent = `Slide ${this.currentSlide + 1} of ${this.totalSlides}`;
  }

  startAutoplay() {
    if (this.totalSlides <= 1) return;
    if (!this.shouldAutoplay()) return;

    this.isPlaying = true;

    this.autoplayInterval = setInterval(() => {
      this.goToSlide((this.currentSlide + 1) % this.totalSlides, false);
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    this.isPlaying = false;
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
    this.isPlaying = false;
  }

  resumeAutoplay() {
    if (!this.shouldAutoplay()) {
      return;
    }
    if (!this.isPlaying && !this.matches(':hover') && !this.matches(':focus-within')) {
      this.startAutoplay();

      const activeDot = this.querySelector('.main-banner__dot--active');
      if (activeDot) {
        const progress = activeDot.querySelector('.main-banner__dot-progress');
        if (progress && !this.prefersReducedMotion) {
          progress.style.animation = 'none';
          progress.style.width = '0';
          void progress.offsetWidth;
          requestAnimationFrame(() => {
            progress.style.animation = '';
          });
        }
      }
    }
  }

  resetAutoplay() {
    this.stopAutoplay();
    this.startAutoplay();
  }
}

if (!customElements.get('sp-main-banner')) {
  customElements.define('sp-main-banner', MainBanner);
}
